import { ChangeDetectionStrategy, Component, computed, input, output } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { RouterLink } from '@angular/router';
import { provideTranslocoScope, TranslocoDirective } from '@jsverse/transloco';
import { RmaIconComponent } from '@rma/generic/ui/icon';
import { AgentComparison } from '@rma/public/compare-agent/private-api/da-compare-agent';
import { AppendReturnUrlParamsPipe } from '@rma/public/generic/util/router-utils';
import { getProfileContactUrl, ProfileContactFormParams } from '@rma/public/profiles/public/domain';
import { ProfileAvatarComponent } from '@rma/public/profiles/public/ui-avatar-legacy';
import { BrandedHeaderComponent } from '@rma/public/profiles/public/ui-branded-header';
import { VerifiedProfileTickComponent } from '@rma/public/profiles/public/ui-verified-profile-tick';
import { AgentPhoneLinkComponent } from '@rma/public/profiles/public/util-phone-link';
import { COMPARE_AGENT_SCOPE } from '../domain/translation-scope';

export type AgentCardDetails = Pick<
  AgentComparison,
  | 'agentCode'
  | 'agentName'
  | 'agencyName'
  | 'agentProfileUrl'
  | 'agencyProfileUrl'
  | 'canContact'
  | 'isBranded'
  | 'isClaimed'
  | 'isTrustedAgent'
  | 'mobile'
  | 'theme'
>;

const TRIXEL_SOURCE_CATEGORY = 'RateMyAgent:CompareAgents:ContactForm';

@Component({
  selector: 'rma-agent-card',
  templateUrl: './agent-card.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [provideTranslocoScope(COMPARE_AGENT_SCOPE)],
  imports: [
    AgentPhoneLinkComponent,
    AppendReturnUrlParamsPipe,
    BrandedHeaderComponent,
    MatButtonModule,
    ProfileAvatarComponent,
    RmaIconComponent,
    RouterLink,
    TranslocoDirective,
    VerifiedProfileTickComponent,
  ],
  host: {
    class: 'relative flex h-full flex-col rounded-lg bg-blue-5',
  },
})
export class AgentCardComponent {
  protected readonly scope = `${COMPARE_AGENT_SCOPE}.agentCard`;
  protected readonly appraisalUrlParams: ProfileContactFormParams = {
    trixelSourceCategory: TRIXEL_SOURCE_CATEGORY,
    enquiryType: 'PropertyAppraisal',
  };
  protected readonly enquireUrlParams: ProfileContactFormParams = {
    trixelSourceCategory: TRIXEL_SOURCE_CATEGORY,
    enquiryType: 'General',
  };

  public readonly agent = input.required<AgentCardDetails>();
  public readonly removeAgent = output<void>();

  public readonly contactUrl = computed(() => {
    const profileUrl = this.agent().agentProfileUrl;
    return profileUrl ? getProfileContactUrl(profileUrl) : null;
  });

  public readonly phoneAnalytics = computed(() => ({
    revealAction: 'Agent Compare Phone numbers Viewed',
    revealLabel: `Agent Compare ${this.agent().agentCode}`,
    callMadeAction: 'Agent Compare Phone Called',
    callMadeLabel: `Mobile - Agent Compare ${this.agent().agentCode}`,
    trixelRevealCategory: 'RateMyAgent-AgentCompare-Reveal',
    trixelCallCategory: 'RateMyAgent-AgentCompare-Call',
  }));
}
